import { FLEX_ACCEPTED_VALUES, SEARCH_MASK_MAX_PRICE, SEARCH_MASK_PRICE_STEP } from '@global-js/constants';
import { boardTypes, roomTypes, transferTypes, roomViews, mostPopularTypes, additionalCheckboxFilterTypes } from '@/js/data/index';
import type { useStore } from '@/components/common/store';
import { groupBy } from '@/js/utils/utils';
import { formatPrice } from '@/js/utils/priceUtils';

import { isHotelList, isHotelPage } from '@/js/utils/searchFilterUtils';
import { roundToNearest } from 'src/utils/number';
import type { useTranslations } from 'src/storybook/src/composables/translations';
import type { AdditionalCheckboxFilters } from '@/interfaces/checkbox-filter-types';
import type { DestinationTypesType, DestinationElement } from '@/components/common/types';

const getDestinationTypeName = (store: ReturnType<typeof useStore>, destinationTypes: DestinationElement[]) => {
	const searchData = store.state.searchMask;
	const locationIdName = (
		store.getters.locationType === 'COUNTRY' || store.getters.locationType === 'REGIONGROUP' ? 'RegionID' : 'CityID'
	) as keyof DestinationTypesType;
	const { destinationTypes: destination } = searchData;
	const { destinationTypeName } = searchData;
	let destName = '';

	if (destinationTypeName) {
		return destinationTypeName;
	}

	// if destinationTypes is set via paramsToForm then only the id is known and it is an array with 1 value
	if (Array.isArray(destination) && destination[0] && destinationTypes.length) {
		const foundType = destinationTypes.find((type) => type[locationIdName] === destination[0]);

		if (foundType) {
			destName = foundType.Name;
		}
	}
	return destName;
};

type FilterItem = {
	label: string;
	value: string | boolean;
	type: string;
	attrType?: string;
};

const groupedTypes = groupBy(
	[...boardTypes, ...roomTypes, ...roomViews, ...transferTypes, ...mostPopularTypes, ...additionalCheckboxFilterTypes],
	'value'
);

type GroupedTypes = typeof groupedTypes;

/**
 * Gets the array of active filters
 */
export function getFilters(store: ReturnType<typeof useStore>, translations: ReturnType<typeof useTranslations>) {
	const { translate } = translations;
	const searchData = store.state.searchMask;
	const currencyType = store.getters['getCurrencyType'];
	const isHotelPageView = isHotelPage();
	const isHotelListView = isHotelList();
	const destinationTypes = store.state.types.destination;
	const destinationNames = getDestinationTypeName(store, destinationTypes);

	// Group all filter types by value for easy lookup

	const filters: FilterItem[] = [];

	// Add board type filters
	filters.push(...mapFilterItems(searchData.boardTypes, groupedTypes, 'boardTypes'));

	// Add room type filters
	filters.push(...mapFilterItems(searchData.roomTypes, groupedTypes, 'roomTypes'));

	// Add room view filters
	if (searchData.roomViews.length > 0) {
		filters.push({ label: 'Meerblick', value: 'FullOcean', type: 'roomViews' });
	}

	// Add most popular filters (if not on hotel page view)
	if (!isHotelPageView) {
		filters.push(...getMostPopularFilters(searchData.mostPopularFilters, groupedTypes));
		filters.push(...getAdditionalCheckboxFilters(searchData.additionalCheckboxFilters, groupedTypes));
	}

	// Add transfer type filters (if not hotel only)
	if (!searchData.onlyHotel) {
		filters.push(...mapFilterItems(searchData.transferTypes, groupedTypes, 'transferTypes'));

		// Add direct flight filter
		if (searchData.directFlight) {
			filters.push({ label: 'Direktflug', value: true, type: 'directFlight' });
		}

		// Add flight time filters
		const { minArrivalTime, minDepartureTime, maxDepartureTime, maxArrivalTime } = searchData;
		if (minArrivalTime > 0 || minDepartureTime > 0 || maxDepartureTime < 24 || maxArrivalTime < 24) {
			filters.push({ label: 'Flugzeiten', value: true, type: 'flight' });
		}
	}

	// Add hotel-specific filters (if not on hotel page view)
	if (!isHotelPageView) {
		// Hotel category filter
		if (searchData.hotelCategory) {
			filters.push({ label: `Hotelkategorie ${searchData.hotelCategory}`, value: true, type: 'hotelCategory' });
		}

		// Recommendation rate filter
		if (searchData.minMeanRecommendationRate) {
			filters.push({
				label: `Weiterempfehlungsrate ${searchData.minMeanRecommendationRate}%`,
				value: true,
				type: 'minMeanRecommendationRate',
			});
		}

		// Max price filter
		if (searchData.maxPrice && searchData.maxPrice !== SEARCH_MASK_MAX_PRICE) {
			filters.push({
				label: `Reisepreis bis ${formatPrice(roundToNearest(searchData.maxPrice, SEARCH_MASK_PRICE_STEP), currencyType)}`,
				value: true,
				type: 'maxPrice',
			});
		}

		// Destination types filter
		if (destinationTypes && destinationNames) {
			filters.push({ label: destinationNames, value: true, type: 'destinationTypes' });
		}
	}

	// Add flex booking filter
	if (FLEX_ACCEPTED_VALUES.includes(searchData.flex as (typeof FLEX_ACCEPTED_VALUES)[number])) {
		filters.push({ label: 'Flexibel buchen', value: true, type: 'flex' });
	}

	// Add deals filter
	if (searchData.deal && isHotelListView) {
		filters.push({ label: translate('filtersSection.dealsOnly.title'), value: true, type: 'deal' });
	}

	// Add operator types filter
	if (searchData.operatorTypes && searchData.operatorTypes.length > 0) {
		filters.push({ label: 'Veranstalter', value: true, type: 'operatorTypes' });
	}

	return filters;
}

/**
 * Maps an array of filter values to filter items
 */
function mapFilterItems(items: string[], types: GroupedTypes, filterType: string): FilterItem[] {
	return items.map((item) => {
		const { label, value } = types[item][0];
		return { label, value, type: filterType };
	});
}

/**
 * Gets most popular filters
 */
function getMostPopularFilters(mostPopular: AdditionalCheckboxFilters, types: GroupedTypes): FilterItem[] {
	if (!mostPopular) return [];

	const mostPopularArray: string[] = [];

	if (mostPopular.hotelAttributes && mostPopular.hotelAttributes.length > 0) {
		mostPopularArray.push(...mostPopular.hotelAttributes);
	}

	if (mostPopular.ratingAttributes && mostPopular.ratingAttributes.length > 0) {
		mostPopularArray.push(...mostPopular.ratingAttributes);
	}

	return mostPopularArray.map((item) => {
		const { label, value, attrType: rawAttrType } = types[item][0];
		const attrType = rawAttrType.charAt(0).toLowerCase() + rawAttrType.slice(1);
		return {
			label,
			value,
			attrType,
			type: 'mostPopularFilters',
		};
	});
}

/**
 * Gets additional checkbox filters
 */
function getAdditionalCheckboxFilters(additionalCheckboxFilters: AdditionalCheckboxFilters, types: GroupedTypes): FilterItem[] {
	if (!additionalCheckboxFilters) return [];

	const filtersArray: string[] = [];

	if (additionalCheckboxFilters.hotelAttributes && additionalCheckboxFilters.hotelAttributes.length > 0) {
		filtersArray.push(...additionalCheckboxFilters.hotelAttributes);
	}

	if (additionalCheckboxFilters.ratingAttributes && additionalCheckboxFilters.ratingAttributes.length > 0) {
		filtersArray.push(...additionalCheckboxFilters.ratingAttributes);
	}

	return filtersArray.map((item) => {
		let attrType = '';
		if ('attrType' in types[item][0]) {
			attrType = types[item][0].attrType;
			attrType = attrType.charAt(0).toLowerCase() + attrType.slice(1);
		}
		const { label, value } = types[item][0];
		return {
			label,
			value,
			attrType,
			type: 'additionalCheckboxFilters',
		};
	});
}
